import RecipeImage from '../components/RecipeImage';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleIcon from '@mui/icons-material/People';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
    fetchCollections,
    updateCollection,
    deleteCollection,
    addToCollection,
    removeFromCollection,
    shareCollection,
    unshareCollection,
    leaveCollection,
} from '../utils/collectionsApi';
import { addToSession, getActiveRecipes, isInSession } from '../utils/cookSession';

const base = () => process.env.REACT_APP_BACKEND_ADDRESS;
const authHeaders = () => ({ 'Authorization': 'Bearer ' + Cookies.get('token') });

export default function CollectionDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [collection, setCollection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allRecipes, setAllRecipes] = useState({});
    const [allUsers, setAllUsers] = useState([]);

    const [renameDialog, setRenameDialog] = useState(false);
    const [renameName, setRenameName] = useState('');
    const [renameDesc, setRenameDesc] = useState('');
    const [shareDialog, setShareDialog] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [leaveDialog, setLeaveDialog] = useState(false);
    const [addDialog, setAddDialog] = useState(false);
    const [addSearch, setAddSearch] = useState('');
    const [cookDialog, setCookDialog] = useState(false);
    const [cookChecked, setCookChecked] = useState({});

    const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;

    const load = useCallback(async () => {
        const data = await fetchCollections();
        const col = data.find(c => String(c.id) === String(id));
        setCollection(col || null);
        setLoading(false);
    }, [id]);

    useEffect(() => {
        load();
        const cached = JSON.parse(sessionStorage.getItem('recipeList'));
        if (cached) {
            const map = {};
            Object.values(cached.recipes || {}).flat().forEach(r => { map[r.id] = r; });
            setAllRecipes(map);
        } else {
            fetch(`${base()}/recipes`, { headers: authHeaders() })
                .then(r => r.json())
                .then(data => {
                    sessionStorage.setItem('recipeList', JSON.stringify(data));
                    const map = {};
                    Object.values(data.recipes || {}).flat().forEach(r => { map[r.id] = r; });
                    setAllRecipes(map);
                })
                .catch(() => {});
        }
    }, [load]);

    const openCookDialog = () => {
        const initial = {};
        collection.recipe_ids.forEach(id => { initial[id] = !isInSession(id); });
        setCookChecked(initial);
        setCookDialog(true);
    };

    const handleCookSession = () => {
        collection.recipe_ids
            .filter(id => cookChecked[id] && allRecipes[id])
            .forEach(id => addToSession(allRecipes[id]));
        setCookDialog(false);
        if (getActiveRecipes().length > 0) navigate('/cook');
    };

    const openShareDialog = () => {
        setShareDialog(true);
        if (!usersLoaded) {
            fetch(`${base()}/users`, { headers: authHeaders() })
                .then(r => r.json())
                .then(data => { setAllUsers(data.users || []); setUsersLoaded(true); })
                .catch(() => {});
        }
    };

    const handleRename = async () => {
        if (!renameName.trim()) return;
        await updateCollection(collection.id, { name: renameName.trim(), description: renameDesc.trim() || null });
        setRenameDialog(false);
        load();
    };

    const handleDelete = async () => {
        await deleteCollection(collection.id);
        navigate('/collections');
    };

    const handleRemoveRecipe = (recipeId) => {
        setCollection(prev => prev ? { ...prev, recipe_ids: prev.recipe_ids.filter(r => r !== recipeId) } : prev);
        removeFromCollection(collection.id, recipeId).catch(() => load());
    };

    const handleAddRecipe = (recipeId) => {
        if (collection.recipe_ids.includes(recipeId)) return;
        setCollection(prev => prev ? { ...prev, recipe_ids: [...prev.recipe_ids, recipeId] } : prev);
        addToCollection(collection.id, recipeId).catch(() => load());
    };

    const handleLeave = async () => {
        await leaveCollection(collection.id);
        navigate('/collections');
    };

    const handleShare = (userId, permission = 'view') => {
        const user = allUsers.find(u => u.id === userId);
        setCollection(prev => {
            if (!prev) return prev;
            const existing = prev.shared_with.find(s => s.shared_with_user_id === userId);
            const updated_shares = existing
                ? prev.shared_with.map(s => s.shared_with_user_id === userId ? { ...s, permission } : s)
                : [...prev.shared_with, { id: Date.now(), shared_with_user_id: userId, shared_with_username: `${user?.first_name} ${user?.last_name}`, permission }];
            return { ...prev, shared_with: updated_shares };
        });
        shareCollection(collection.id, userId, permission).catch(() => load());
    };

    const handleUnshare = (userId) => {
        setCollection(prev => prev ? { ...prev, shared_with: prev.shared_with.filter(s => s.shared_with_user_id !== userId) } : prev);
        unshareCollection(collection.id, userId).catch(() => load());
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress />
        </Box>
    );

    if (!collection) return (
        <Container sx={{ mt: 5 }}>
            <Typography>Collection not found.</Typography>
            <Button onClick={() => navigate('/collections')} sx={{ mt: 2 }}>Back to Collections</Button>
        </Container>
    );

    const isOwner = collection.my_permission === 'owner';
    const canEdit = collection.my_permission === 'owner' || collection.my_permission === 'edit';
    const filteredUsers = allUsers.filter(u =>
        u.id !== currentUserId &&
        `${u.first_name} ${u.last_name} ${u.username}`.toLowerCase().includes(userSearch.toLowerCase())
    );

    return (
        <Container sx={{ mt: 4, width: '90%', pb: 12 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Tooltip title="Back to Collections">
                    <IconButton size="small" onClick={() => navigate('/collections')}>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>
                {collection.is_favorites && <FavoriteIcon sx={{ color: 'error.main' }} />}
                <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
                    {collection.name}
                </Typography>
                {isOwner && !collection.is_favorites && (
                    <Tooltip title="Edit collection">
                        <IconButton size="small" onClick={() => { setRenameName(collection.name); setRenameDesc(collection.description || ''); setRenameDialog(true); }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canEdit && (
                    <Tooltip title="Add recipes">
                        <IconButton size="small" onClick={() => { setAddSearch(''); setAddDialog(true); }}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {collection.recipe_ids.length > 0 && (
                    <Tooltip title="Cook this collection">
                        <IconButton size="small" onClick={openCookDialog}>
                            <OutdoorGrillIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {isOwner && (
                    <Tooltip title="Share">
                        <IconButton size="small" onClick={openShareDialog}>
                            <PersonAddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {isOwner && !collection.is_favorites && (
                    <Tooltip title="Delete collection">
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog(true)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {!isOwner && (
                    <Button size="small" color="error" onClick={() => setLeaveDialog(true)}>Leave</Button>
                )}
            </Box>

            {/* Sub-header */}
            <Box sx={{ pl: 5, mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {collection.recipe_ids.length} {collection.recipe_ids.length === 1 ? 'recipe' : 'recipes'}
                    {!isOwner && ` · Shared by ${collection.owner_username}`}
                    {isOwner && collection.shared_with?.length > 0 && ` · Shared with ${collection.shared_with.length} ${collection.shared_with.length === 1 ? 'person' : 'people'}`}
                </Typography>
                {collection.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {collection.description}
                    </Typography>
                )}
            </Box>

            {/* Recipes grid */}
            {collection.recipe_ids.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 2 }}>
                    <Typography color="text.secondary">No recipes in this collection yet.</Typography>
                    {canEdit && (
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setAddSearch(''); setAddDialog(true); }}>
                            Add Recipes
                        </Button>
                    )}
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {[...collection.recipe_ids]
                        .sort((a, b) => (allRecipes[a]?.title || '').localeCompare(allRecipes[b]?.title || ''))
                        .map(recipeId => {
                        const r = allRecipes[recipeId];
                        return (
                            <Grid item xs={6} sm={4} md={3} key={recipeId}>
                                <Box sx={{
                                    borderRadius: 2, overflow: 'hidden', border: '1px solid',
                                    borderColor: 'divider', position: 'relative',
                                    '&:hover': { boxShadow: 3 },
                                }}>
                                    <Link to={`/recipe/${recipeId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <RecipeImage
                                            loading="lazy"
                                            src={r?.download_image_url || '/anchovie.jpg'}
                                            alt={r?.title || `Recipe #${recipeId}`}
                                            sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }}
                                        />
                                        <Box sx={{ p: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                {r?.title || `Recipe #${recipeId}`}
                                            </Typography>
                                        </Box>
                                    </Link>
                                    {canEdit && (
                                        <Tooltip title="Remove from collection">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveRecipe(recipeId)}
                                                sx={{ position: 'absolute', top: 4, right: 4, color: 'white', bgcolor: 'rgba(0,0,0,0.35)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' }, p: 0.25 }}
                                            >
                                                <CloseIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Edit dialog */}
            <Dialog open={renameDialog} onClose={() => setRenameDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon sx={{ color: 'primary.main' }} />Edit Collection</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" label="Name" value={renameName}
                        onChange={e => setRenameName(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        fullWidth size="small" label="Description (optional)" value={renameDesc}
                        onChange={e => setRenameDesc(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleRename()}
                        multiline rows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleRename}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirm dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><DeleteIcon sx={{ color: 'error.main' }} />Delete "{collection.name}"?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will permanently delete the collection. Recipes themselves will not be affected.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Leave dialog */}
            <Dialog open={leaveDialog} onClose={() => setLeaveDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><ExitToAppIcon sx={{ color: 'error.main' }} />Leave "{collection.name}"?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        You'll lose access to this collection. The owner can re-share it with you later.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLeaveDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleLeave}>Leave</Button>
                </DialogActions>
            </Dialog>

            {/* Add recipes dialog */}
            <Dialog open={addDialog} onClose={() => setAddDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><AddIcon sx={{ color: 'primary.main' }} />Add Recipes</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" placeholder="Search recipes…" value={addSearch}
                        onChange={e => setAddSearch(e.target.value)}
                        sx={{ mt: 1, mb: 1 }}
                    />
                    <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                        {Object.values(allRecipes)
                            .filter(r => r.title?.toLowerCase().includes(addSearch.toLowerCase()))
                            .sort((a, b) => a.title.localeCompare(b.title))
                            .map(r => {
                                const already = collection.recipe_ids.includes(r.id);
                                return (
                                    <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', py: 0.75, gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <RecipeImage
                                            src={r.download_image_url || '/anchovie.jpg'}
                                            alt={r.title}
                                            sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                        />
                                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }} noWrap>{r.title}</Typography>
                                        <Tooltip title={already ? 'Already in collection' : 'Add to collection'}>
                                            <span>
                                                <IconButton size="small" onClick={() => handleAddRecipe(r.id)} disabled={already} sx={{ color: already ? 'success.main' : 'action.active' }}>
                                                    {already ? <CheckIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                );
                            })}
                        {Object.values(allRecipes).filter(r => r.title?.toLowerCase().includes(addSearch.toLowerCase())).length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No recipes found.</Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialog(false)}>Done</Button>
                </DialogActions>
            </Dialog>

            {/* Share dialog */}
            <Dialog open={shareDialog} onClose={() => { setShareDialog(false); setUserSearch(''); }} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><PeopleIcon sx={{ color: 'primary.main' }} />Share "{collection.name}"</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" placeholder="Search users…" value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                        sx={{ mt: 1, mb: 1 }}
                    />
                    {filteredUsers.map(u => {
                        const existingShare = collection.shared_with?.find(s => s.shared_with_user_id === u.id);
                        return (
                            <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', py: 0.75, gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{u.first_name} {u.last_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">@{u.username}</Typography>
                                </Box>
                                {existingShare ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        <ToggleButtonGroup
                                            size="small"
                                            exclusive
                                            value={existingShare.permission}
                                            onChange={(_, val) => { if (val) handleShare(u.id, val); }}
                                        >
                                            <ToggleButton value="view" sx={{ py: 0.25, px: 1, fontSize: 11 }}>View</ToggleButton>
                                            <ToggleButton value="edit" sx={{ py: 0.25, px: 1, fontSize: 11 }}>Edit</ToggleButton>
                                        </ToggleButtonGroup>
                                        <Button size="small" color="error" sx={{ py: 0, minWidth: 0 }} onClick={() => handleUnshare(u.id)}>Remove</Button>
                                    </Box>
                                ) : (
                                    <Button size="small" onClick={() => handleShare(u.id, 'view')}>Add</Button>
                                )}
                            </Box>
                        );
                    })}
                    {filteredUsers.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No users found.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setShareDialog(false); setUserSearch(''); }}>Done</Button>
                </DialogActions>
            </Dialog>

            {/* Cook collection dialog */}
            <Dialog open={cookDialog} onClose={() => setCookDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><OutdoorGrillIcon sx={{ color: 'primary.main' }} />Cook "{collection?.name}"</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Select which recipes to add to your cooking session.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => {
                            const all = {};
                            collection.recipe_ids.forEach(id => { all[id] = !isInSession(id); });
                            setCookChecked(all);
                        }}>Select all</Button>
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => {
                            const none = {};
                            collection.recipe_ids.forEach(id => { none[id] = false; });
                            setCookChecked(none);
                        }}>Deselect all</Button>
                    </Box>
                    {collection?.recipe_ids.map(id => {
                        const recipe = allRecipes[id];
                        if (!recipe) return null;
                        const alreadyIn = isInSession(id);
                        return (
                            <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', opacity: alreadyIn ? 0.5 : 1 }}>
                                <RecipeImage
                                    src={recipe.download_image_url || '/anchovie.jpg'}
                                    alt={recipe.title}
                                    sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!cookChecked[id]}
                                            onChange={e => setCookChecked(prev => ({ ...prev, [id]: e.target.checked }))}
                                            size="small"
                                            disabled={alreadyIn}
                                        />
                                    }
                                    label={
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" noWrap>{recipe.title}</Typography>
                                            {alreadyIn && <Typography variant="caption" color="text.secondary">Already in session</Typography>}
                                        </Box>
                                    }
                                    sx={{ flex: 1, m: 0, minWidth: 0 }}
                                />
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCookDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        startIcon={<OutdoorGrillIcon />}
                        onClick={handleCookSession}
                        disabled={!Object.values(cookChecked).some(Boolean)}
                    >
                        {getActiveRecipes().length > 0 ? 'Add to Session' : 'Start Cooking'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
